import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Bell, ChevronDown, Compass, Heart, LogOut, MessageCircle, Search, ShoppingBag,
  SlidersHorizontal, UserRound, UserRoundPlus, X
} from 'lucide-react';
import AuthContext from '../../context/AuthContext';
import NotificationIcon from '../notifications/NotificationIcon';
import axios, { getUploadUrl } from '../../api/axios';
import ThemeToggle from './ThemeToggle';

function HeaderIcon({ name }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    'aria-hidden': 'true'
  };

  if (name === 'search') {
    return (
      <svg {...common}>
        <path d="M21 21l-4.2-4.2m1.2-5.3a6.5 6.5 0 1 1-13 0 6.5 6.5 0 0 1 13 0Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  if (name === 'bell') {
    return (
      <svg {...common}>
        <path d="M18 8a6 6 0 1 0-12 0c0 7-3 6-3 9h18c0-3-3-2-3-9Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M10 20a2.3 2.3 0 0 0 4 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }

  if (name === 'cart') {
    return (
      <svg {...common}>
        <path d="M4 5h2l2 11h9.5l2-7.5H7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M10 20h.1M17 20h.1" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      </svg>
    );
  }

  if (name === 'menu') {
    return (
      <svg {...common}>
        <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
      </svg>
    );
  }

  if (name === 'upload') {
    return (
      <svg {...common}>
        <path d="M12 15V4M12 4 8 8m4-4 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  return null;
}

// Cart UI removed from header (cart handled on /cart page)

function NotificationPreviewRow({ item, onRead }) {
  const unseenStyle = item.unread ? { boxShadow: '0 0 0 3px rgba(255,200,0,0.12)', borderRadius: 6 } : undefined;
  return (
    <button type="button" className={`notification-dropdown-row ${item.unread ? 'is-unseen' : ''}`} style={unseenStyle} onClick={() => onRead(item)}>
      <span className={`notification-icon-bubble ${item.tone}`}>
        <NotificationIcon name={item.icon} />
      </span>
      <span>
        <strong>{item.title}</strong>
        <small>{item.text}</small>
      </span>
      <time>{item.time}</time>
      <em className={item.priority ? 'is-priority' : ''} aria-hidden="true" />
    </button>
  );
}

function NotificationDropdown({ items, onClose, onMarkAllRead, onRead }) {
  const [activeFilter, setActiveFilter] = useState('all');
  const visibleFilters = [{ id: 'all', label: 'All' }, { id: 'orders', label: 'Orders' }, { id: 'market', label: 'Market' }, { id: 'system', label: 'System' }];
  const visibleItems = activeFilter === 'all' || activeFilter === 'unread'
    ? items
    : items.filter(item => item.category === activeFilter);
  const unreadCount = items.filter(item => item.unread).length;

  return (
    <section className="notification-dropdown" aria-label="Notifications preview">
      <header>
        <div>
          <h2>Notifications</h2>
          <span>{unreadCount}</span>
        </div>
        <button type="button" onClick={onMarkAllRead}>
          <NotificationIcon name="check" size={15} />
          Mark all as read
        </button>
      </header>

      <div className="notification-dropdown-tabs" role="tablist" aria-label="Notification filters">
        {visibleFilters.map(filter => (
          <button
            key={filter.id}
            type="button"
            className={activeFilter === filter.id ? 'active' : ''}
            onClick={() => setActiveFilter(filter.id)}
          >
            {filter.label}
            {filter.badge && <span>{filter.badge}</span>}
          </button>
        ))}
      </div>

      <div className="notification-dropdown-list">
        {visibleItems.map(item => <NotificationPreviewRow key={item.id} item={item} onRead={onRead} />)}
      </div>

      <Link className="notification-dropdown-all" to="/notifications" onClick={onClose}>
        View all notifications <span aria-hidden="true">-&gt;</span>
      </Link>
    </section>
  );
}

export default function Header() {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState('');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const notificationRef = useRef(null);
  const accountRef = useRef(null);
  const notificationCount = notifications.filter(item => item.unread).length;
  const notificationOpen = openMenu === 'notifications';
  const profilePhotoUrl = user?.profilePhoto ? getUploadUrl(user.profilePhoto) : '/getsocs-logo.png';
  const currentPath = location.pathname;
  const isActive = (path) => path === '/'
    ? currentPath === '/'
    : currentPath === path || currentPath.startsWith(`${path}/`);
  const closeMenus = () => {
    setOpenMenu('');
    setMobileNavOpen(false);
  };

  async function markNotificationRead(item) {
    if (item.unread) {
      setNotifications(previous => previous.map(notification => (
        notification.id === item.id ? { ...notification, unread: false } : notification
      )));
      try {
        await axios.patch(`/notifications/${encodeURIComponent(item.id)}/mark-read`);
      } catch (error) {
        setNotifications(previous => previous.map(notification => (
          notification.id === item.id ? { ...notification, unread: true } : notification
        )));
      }
    }
    setOpenMenu('');
    setMobileNavOpen(false);
    navigate('/notifications');
  }

  useEffect(() => {
    setOpenMenu('');
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let mounted = true;

    async function loadHeaderData() {
      try {
        const notificationsRes = await axios.get('/notifications');
        if (!mounted) return;
        const apiNotifications = Array.isArray(notificationsRes?.data?.data) ? notificationsRes.data.data : [];
        setNotifications(apiNotifications.length ? apiNotifications : []);
      } catch (error) {
        if (mounted) setNotifications([]);
      }
    }

    if (user) loadHeaderData();
    else setNotifications([]);

    return () => { mounted = false; };
  }, [user]);

  useEffect(() => {
    return undefined;
  }, []);

  useEffect(() => {
    if (!openMenu) return undefined;

    function handleOutsideClick(event) {
      const clickedInsideOpenMenu = openMenu === 'notifications'
        ? notificationRef.current?.contains(event.target)
        : accountRef.current?.contains(event.target);
      if (clickedInsideOpenMenu) return;
      setOpenMenu('');
    }

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [openMenu]);

  const navItems = [
    { label: 'Explore', to: '/', icon: Compass },
    { label: 'Orders', to: '/orders', icon: ShoppingBag },
    { label: 'Studio', to: '/upload', icon: SlidersHorizontal },
    { label: 'Saved', to: '/favorites', icon: Heart },
    { label: 'Messages', to: '/messages', icon: MessageCircle },
    { label: 'Updates', to: '/notifications', icon: Bell, badge: notificationCount }
  ];

  return (
    <header className="site-header">
      <Link className="brand" to="/" aria-label="GetSocs home" onClick={closeMenus}>
        <img className="brand-wordmark" src="/getsocs-wordmark.png" alt="" />
      </Link>

      {mobileNavOpen && (
        <button
          className="mobile-nav-backdrop"
          type="button"
          aria-label="Close navigation menu"
          onClick={closeMenus}
        />
      )}
      <nav className={`main-nav ${mobileNavOpen ? 'nav-open' : ''}`} aria-label="Main navigation">
        <div className="mobile-nav-header">
          <img className="brand-wordmark" src="/getsocs-wordmark.png" alt="GetSocs" />
          <button className="icon-button" type="button" onClick={closeMenus} aria-label="Close menu">
            <X size={18} />
          </button>
        </div>
        {navItems.map(({ label, to, icon: Icon, badge }) => (
          <Link
            key={to}
            className={`nav-link ${isActive(to) ? 'active' : ''}`}
            to={to}
            onClick={closeMenus}
          >
            <Icon size={16} />
            {label}
            {badge > 0 && <span className="nav-badge">{badge}</span>}
          </Link>
        ))}
      </nav>

      <div className="header-actions">
        <ThemeToggle compact className="header-theme-toggle" />
        <button
          className="header-search"
          type="button"
          aria-label="Open marketplace search"
          onClick={() => navigate('/products')}
        >
          <Search size={17} />
        </button>
        <div className={`header-notifications ${notificationOpen ? 'is-open' : ''}`} ref={notificationRef}>
          <button
            className="header-icon-link"
            type="button"
            title="Notifications"
            aria-label={`Notifications${notificationCount ? ` (${notificationCount} unread)` : ''}`}
            aria-expanded={notificationOpen}
            onClick={() => setOpenMenu(menu => menu === 'notifications' ? '' : 'notifications')}
          >
            <Bell size={17} />
            {notificationCount > 0 && <i>{notificationCount}</i>}
          </button>
          {notificationOpen && (
            <NotificationDropdown
              items={notifications}
              onClose={() => setOpenMenu('')}
              onRead={markNotificationRead}
              onMarkAllRead={async () => {
                try {
                  await axios.post('/notifications/mark-all-read');
                  setNotifications(prev => prev.map(n => ({ ...n, unread: false })));
                } catch (e) {}
              }}
            />
          )}
        </div>
        {user ? (
          <>
            <Link className="button button-dark header-join" to="/upload">
              <SlidersHorizontal size={15} /> <span>List Asset</span>
            </Link>
            <div className="account-menu" ref={accountRef}>
              <button
                className="account-trigger"
                type="button"
                onClick={() => setOpenMenu(menu => menu === 'account' ? '' : 'account')}
                aria-expanded={openMenu === 'account'}
              >
                <span className="avatar avatar-small">
                  <img src={profilePhotoUrl} alt="" />
                </span>
                <span>{user.username || user.name || 'Account'}</span>
              </button>
              {openMenu === 'account' && (
                <div className="account-dropdown">
                  <div className="dropdown-user">
                    <strong>{user.name || user.username}</strong>
                    <small>{user.email}</small>
                  </div>
                  <Link to="/profile" onClick={closeMenus}><UserRound size={15} /> Account settings</Link>
                  <Link to="/orders" onClick={closeMenus}><ShoppingBag size={15} /> Orders</Link>
                  <button type="button" onClick={() => { closeMenus(); logout(); }}>
                    <LogOut size={15} /> Sign out
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="account-menu guest-account-menu" ref={accountRef}>
            <button
              className="account-trigger guest-account-trigger"
              type="button"
              aria-label="Open account menu"
              onClick={() => setOpenMenu(menu => menu === 'account' ? '' : 'account')}
              aria-expanded={openMenu === 'account'}
              aria-haspopup="menu"
            >
              <UserRound size={17} aria-hidden="true" />
              <span className="guest-account-label">Account</span>
              <ChevronDown
                size={15}
                aria-hidden="true"
                className={openMenu === 'account' ? 'icon-rotated' : ''}
              />
            </button>
            {openMenu === 'account' && (
              <div className="account-dropdown guest-account-dropdown" role="menu" aria-label="Account options">
                <div className="dropdown-user">
                  <strong>Welcome to GetSocs</strong>
                  <small>Sign in or create an account to continue.</small>
                </div>
                <Link to="/login" role="menuitem" onClick={closeMenus}>
                  <UserRound size={15} /> Sign in
                </Link>
                <Link to="/register" role="menuitem" onClick={closeMenus}>
                  <UserRoundPlus size={15} /> Create account
                </Link>
              </div>
            )}
          </div>
        )}
        <button
          className="menu-toggle"
          type="button"
          onClick={() => setMobileNavOpen(true)}
          aria-label="Open navigation menu"
          aria-expanded={mobileNavOpen}
        >
          <HeaderIcon name="menu" />
        </button>
      </div>
    </header>
  );
}
